import {
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { useDetectOutsideClick } from '../../hooks/useDetectOutsideClick'
import { ChevronDownIcon } from '../../icons/inputIcons'

export interface SearchableSelectProps<T> {
  items: T[]
  value: T | null
  onSelect: (item: T | null) => void
  getKey: (item: T) => string | number
  getLabel: (item: T) => string
  /** Optional override for what gets searched (defaults to getLabel). */
  getSearchText?: (item: T) => string
  /** Optional custom item renderer for richer rows. */
  renderItem?: (item: T, isSelected: boolean) => ReactNode

  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  leadingIcon?: ReactNode | null
  className?: string

  /** When true, shows a "clear" virtual row at the top of the dropdown that selects null. */
  allowClear?: boolean
  /** Label for the clear/all row. */
  clearLabel?: string
  /** Label for empty state. */
  emptyLabel?: string
}


export function SearchableSelect<T>({
  items,
  value,
  onSelect,
  getKey,
  getLabel,
  getSearchText,
  renderItem,
  placeholder = '',
  disabled = false,
  readOnly = false,
  leadingIcon,
  className,
  allowClear = false,
  clearLabel = '',
  emptyLabel = '—',
}: SearchableSelectProps<T>) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const renderedLeading = leadingIcon === null ? null : leadingIcon ?? null

  useDetectOutsideClick(wrapperRef, () => setOpen(false))

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    const probe = getSearchText ?? getLabel
    return items.filter((item) => probe(item).toLowerCase().includes(q))
  }, [items, query, getSearchText, getLabel])

  const valueLabel = value ? getLabel(value).trim() : ''

  if (readOnly) {
    return (
      <Wrapper className={className} $hasLeading={!!renderedLeading} $readOnly>
        {renderedLeading && <IconSlot aria-hidden>{renderedLeading}</IconSlot>}
        <ReadonlyText>{valueLabel || placeholder}</ReadonlyText>
      </Wrapper>
    )
  }

  const displayValue = open ? query : valueLabel

  const handleSelect = (item: T | null) => {
    onSelect(item)
    setQuery('')
    setOpen(false)
    inputRef.current?.blur()
  }

  return (
    <Wrapper
      ref={wrapperRef}
      className={className}
      $hasLeading={!!renderedLeading}
      $disabled={disabled}
    >
      {renderedLeading && <IconSlot aria-hidden>{renderedLeading}</IconSlot>}
      <Field
        ref={inputRef}
        type='text'
        autoComplete='off'
        placeholder={placeholder}
        value={displayValue}
        disabled={disabled}
        onFocus={() => {
          if (disabled) return
          setQuery('')
          setOpen(true)
        }}
        onChange={(e) => {
          setQuery(e.target.value)
          if (!open) setOpen(true)
        }}
        onBlur={() => {
          setQuery('')
          setOpen(false)
        }}
      />
      <Chev
        type='button'
        aria-label='toggle'
        tabIndex={-1}
        $open={open}
        onClick={() => {
          if (disabled) return
          if (!open) {
            inputRef.current?.focus()
          } else {
            setOpen(false)
            inputRef.current?.blur()
          }
        }}
      >
        <ChevronDownIcon />
      </Chev>

      {open && (
        <Dropdown role='listbox'>
          {allowClear && (
            <Item
              role='option'
              aria-selected={value === null}
              $active={value === null}
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleSelect(null)
              }}
            >
              {clearLabel}
            </Item>
          )}
          {filtered.length === 0 && !allowClear ? (
            <Empty>{emptyLabel}</Empty>
          ) : (
            filtered.map((item) => {
              const key = getKey(item)
              const isSelected = value !== null && getKey(value) === key
              return (
                <Item
                  key={String(key)}
                  role='option'
                  aria-selected={isSelected}
                  $active={isSelected}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleSelect(item)
                  }}
                >
                  {renderItem ? renderItem(item, isSelected) : getLabel(item)}
                </Item>
              )
            })
          )}
        </Dropdown>
      )}
    </Wrapper>
  )
}

export default SearchableSelect

const Wrapper = styled.div<{ $hasLeading: boolean; $disabled?: boolean; $readOnly?: boolean }>`
  position: relative;
  width: 100%;
  min-height: 2.875rem;
  display: grid;
  grid-template-columns: ${({ $hasLeading }) => ($hasLeading ? 'auto 1fr auto' : '1fr auto')};
  align-items: center;
  gap: 0.625rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 0.875rem;
  padding: 0 0.75rem 0 0.5rem;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  cursor: ${({ $readOnly, $disabled }) => ($readOnly || $disabled ? 'default' : 'text')};

  &:hover {
    border-color: ${({ theme, $readOnly, $disabled }) => ($readOnly || $disabled ? undefined : theme.colors.inputBorderHover)};
  }

  &:focus-within {
    border-color: ${({ theme, $readOnly, $disabled }) => ($readOnly || $disabled ? undefined : theme.colors.ink)};
    box-shadow: ${({ theme, $readOnly, $disabled }) => ($readOnly || $disabled ? 'none' : theme.shadows.focus)};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 2.5rem;
    padding: 0 0.625rem 0 0.375rem;
    gap: 0.5rem;
  }
`

const IconSlot = styled.span`
  color: ${({ theme }) => theme.colors.icon};
  display: grid;
  place-items: center;
  width: 1.25rem;
  height: 1.25rem;

  svg {
    width: 1.05rem;
    height: 1.05rem;
    flex-shrink: 0;
  }
`

const Field = styled.input`
  border: 0;
  outline: 0;
  background: transparent;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  width: 100%;
  padding: 0.375rem 0;
  direction: rtl;

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  &:disabled {
    background: transparent;
    color: ${({ theme }) => theme.colors.ink2};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`

const ReadonlyText = styled.div`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  padding: 0.375rem 0;
  direction: rtl;
  text-align: start;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`

const Chev = styled.button<{ $open: boolean }>`
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.icon};
  width: 1.5rem;
  height: 1.5rem;
  display: grid;
  place-items: center;
  cursor: pointer;
  border-radius: 6px;
  transition:
    transform ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});

  &:hover {
    color: ${({ theme }) => theme.colors.ink};
    background: ${({ theme }) => theme.colors.line2};
  }

  svg { width: 0.875rem; height: 0.875rem; }
`


const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  inset-inline-start: 0;
  inset-inline-end: 0;
  z-index: 20;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 0.875rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px;
  direction: rtl;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar { width: 0; height: 0; }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-height: 11rem;
  }
`

const Item = styled.div<{ $active: boolean }>`
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  text-align: start;
  background: ${({ $active, theme }) => ($active ? theme.colors.accentSoft : 'transparent')};
  transition: background ${({ theme }) => theme.motion.fast} ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  &:hover {
    background: ${({ theme, $active }) => ($active ? theme.colors.accentSoft : theme.colors.line2)};
  }
`

const Empty = styled.div`
  padding: 0.75rem;
  color: ${({ theme }) => theme.colors.ink3};
  text-align: start;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
`
